import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import { Switch } from "./ui/switch";
import { UserPlus, Edit, Trash2, KeyRound, Loader2, ShieldCheck, User } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UsersManagement() {
  const { user: me, isAdmin } = useAuth();
  const { toast } = useToast();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(null);
  const [pwOpen, setPwOpen] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const refresh = async () => {
    try {
      const res = await axios.get(`${API}/users`);
      setUsers(res.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (isAdmin) refresh(); }, [isAdmin]);

  if (!isAdmin) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-slate-500">
          <ShieldCheck className="w-10 h-10 mx-auto mb-2 opacity-50" />
          Only administrators can manage users.
        </CardContent>
      </Card>
    );
  }

  const toggleActive = async (u, v) => {
    try {
      await axios.patch(`${API}/users/${u.id}`, { active: v });
      refresh();
    } catch (e) {
      toast({ title: "Error", description: e?.response?.data?.detail || "Failed", variant: "destructive" });
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>User Accounts</CardTitle>
          <CardDescription>Add teachers or admins who can sign into the system</CardDescription>
        </div>
        <Button onClick={() => setAddOpen(true)} className="gradient-blue-mild text-white">
          <UserPlus className="w-4 h-4 mr-2" />Add User
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-2 text-left">User</th>
                  <th className="px-4 py-2 text-left">Username</th>
                  <th className="px-4 py-2 text-left">Role</th>
                  <th className="px-4 py-2 text-left">Active</th>
                  <th className="px-4 py-2 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full gradient-blue-mild text-white flex items-center justify-center font-semibold text-sm" dir="rtl">{u.name?.[0] || "U"}</div>
                        <div>
                          <div className="font-medium text-slate-800" dir="rtl">{u.name}</div>
                          {u.id === me?.id && <div className="text-[10px] text-blue-600">You</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600 font-mono">{u.username}</td>
                    <td className="px-4 py-3">
                      {u.role === "admin" ? (
                        <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100"><ShieldCheck className="w-3 h-3 mr-1" />Admin</Badge>
                      ) : (
                        <Badge variant="secondary"><User className="w-3 h-3 mr-1" />User</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Switch checked={!!u.active} disabled={u.id === me?.id} onCheckedChange={(v) => toggleActive(u, v)} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" onClick={() => setEditOpen(u)} title="Edit"><Edit className="w-4 h-4" /></Button>
                        <Button size="icon" variant="ghost" onClick={() => setPwOpen(u)} title="Reset Password"><KeyRound className="w-4 h-4" /></Button>
                        {u.id !== me?.id && (
                          <Button size="icon" variant="ghost" onClick={() => setConfirmDel(u)} title="Delete">
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      <AddUserDialog open={addOpen} onOpenChange={setAddOpen} onDone={refresh} />
      <EditUserDialog user={editOpen} onOpenChange={() => setEditOpen(null)} onDone={refresh} />
      <ResetPasswordDialog user={pwOpen} onOpenChange={() => setPwOpen(null)} />

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this user?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove <span dir="rtl" className="font-medium">{confirmDel?.name}</span> ({confirmDel?.username}). Their previously signed passes will still show their name.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={async () => {
              try {
                await axios.delete(`${API}/users/${confirmDel.id}`);
                toast({ title: "User deleted" });
                setConfirmDel(null);
                refresh();
              } catch (e) {
                toast({ title: "Error", description: e?.response?.data?.detail || "Failed", variant: "destructive" });
              }
            }}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

function AddUserDialog({ open, onOpenChange, onDone }) {
  const [form, setForm] = useState({ username: "", password: "", name: "", role: "user" });
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const reset = () => setForm({ username: "", password: "", name: "", role: "user" });

  const submit = async () => {
    if (!form.username || !form.password || !form.name) {
      toast({ title: "Error", description: "Please fill in all fields", variant: "destructive" });
      return;
    }
    if (form.password.length < 6) {
      toast({ title: "Error", description: "Password must be at least 6 characters", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/users`, form);
      toast({ title: "User added", description: `${form.name} can now sign in as ${form.username}` });
      onOpenChange(false);
      reset();
      onDone();
    } catch (e) {
      toast({ title: "Error", description: e?.response?.data?.detail || "Failed", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add New User</DialogTitle>
          <DialogDescription>Create a new account that can sign into the system</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">Display Name (Hebrew/Yiddish OK)</label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. הרב שלמה וויס" dir="rtl" />
            <div className="text-xs text-slate-500 mt-1">Shown as the signer on late passes</div>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">Username (for login)</label>
            <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="e.g. sweiss" autoComplete="off" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">Password</label>
            <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="At least 6 characters" autoComplete="new-password" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">Role</label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="user">User (can create passes)</SelectItem>
                <SelectItem value="admin">Admin (can also manage users &amp; settings)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="gradient-blue-mild text-white">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
            Add User
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditUserDialog({ user, onOpenChange, onDone }) {
  const [form, setForm] = useState({ name: "", role: "user" });
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  const { user: me, refreshMe } = useAuth();

  useEffect(() => {
    if (user) setForm({ name: user.name || "", role: user.role || "user" });
  }, [user]);

  if (!user) return null;

  const submit = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/users/${user.id}`, form);
      toast({ title: "User updated" });
      if (user.id === me?.id) await refreshMe();
      onOpenChange();
      onDone();
    } catch (e) {
      toast({ title: "Error", description: e?.response?.data?.detail || "Failed", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!user} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit User</DialogTitle>
          <DialogDescription>Update <span className="font-mono">{user.username}</span></DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">Display Name</label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} dir="rtl" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">Role</label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })} disabled={user.id === me?.id}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="user">User</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
            {user.id === me?.id && <div className="text-xs text-slate-500 mt-1">You can't change your own role</div>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onOpenChange}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="gradient-blue-mild text-white">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Edit className="w-4 h-4 mr-2" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ user, onOpenChange }) {
  const [pw, setPw] = useState("");
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => { setPw(""); }, [user]);

  if (!user) return null;

  const submit = async () => {
    if (pw.length < 6) {
      toast({ title: "Error", description: "Password must be at least 6 characters", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      await axios.patch(`${API}/users/${user.id}`, { password: pw });
      toast({ title: "Password reset", description: `${user.name} can sign in with the new password` });
      onOpenChange();
    } catch (e) {
      toast({ title: "Error", description: e?.response?.data?.detail || "Failed", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!user} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reset Password</DialogTitle>
          <DialogDescription>Set a new password for <span dir="rtl" className="font-medium">{user.name}</span> ({user.username})</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">New Password</label>
            <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 6 characters" autoComplete="new-password" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onOpenChange}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="gradient-blue-mild text-white">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
            Reset Password
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
